/**
 * Made-to-measure order configurator.
 *
 * Lets a customer choose lining, buttons and (later) other option groups before
 * adding a garment to the cart. Each chosen option is a real product, so it
 * enters the cart as its own line at its catalogue price, tied to the garment by
 * a shared _config_id. This store is on Shopify Basic — no Scripts, no checkout
 * extensibility — so add-on lines are the only way to charge for an option.
 *
 * WHAT APPLIES IS DECIDED BY THE SELECTED VARIANT.
 * A suit's "Trousers" variant takes neither a lining nor suit buttons; a
 * knitwear "Crew neck" takes no buttons while "Half button" does. The variant
 * rules come from the section; the product type only gates what the catalogue
 * can offer at all. Changing the variant re-resolves the groups live and drops
 * any selection that no longer applies.
 *
 * No copy lives in this file. Everything customer-facing arrives from the
 * section's JSON island, because the theme translates through langify and
 * langify only rewrites text nodes rendered by Liquid.
 */
(function () {
  'use strict';

  var CATALOG_CACHE_PREFIX = 'possen:oc:catalog:v2:';
  var CATALOG_TTL_MS = 30 * 60 * 1000;
  var TAG_FOR = 'config:for:';
  var TAG_GROUP = 'config:group:';

  /** Cards past this point skip the stagger — 197 linings must not cascade. */
  var STAGGER_LIMIT = 14;

  var root = null;
  var dialog = null;
  var config = null;
  var rules = [];
  var allGroups = [];
  var groups = [];
  var selection = {};
  var catalogs = {};
  var activeGroup = null;
  var activeFilter = null;
  var lastFocus = null;
  var currentVariant = null;
  var submitting = false;
  var cartLineKey = null;

  // --- boot ---------------------------------------------------------------

  function init() {
    root = document.querySelector('[data-oc-root]');
    if (!root) return;

    var island = root.parentNode.querySelector('[data-oc-config]');
    if (!island) return;

    try {
      config = JSON.parse(island.textContent);
    } catch (error) {
      return;
    }

    // Escape the theme's stacking contexts. jquery.mmenu wraps the whole page in
    // div.mm-page.mm-slideout with `position: relative; z-index: 1`, which caps
    // everything inside it — the dialog's z-index cannot beat the site header
    // from in there, however large it is, and the header paints over the title.
    // Re-parenting to <body> is the only reliable fix. Config is already parsed
    // above, so losing the island sibling does not matter.
    if (root.parentNode !== document.body) document.body.appendChild(root);

    dialog = root.querySelector('[data-oc-dialog]');
    allGroups = config.groups || [];
    rules = parseRules(config.variantRulesRaw || '');

    root.querySelector('[data-oc-title]').textContent = config.labels.title;
    root.querySelector('[data-oc-done]').textContent = config.labels.done;

    bind();
    watchVariant();
    resolveGroups();
    initCart();
  }

  // --- variant rules ------------------------------------------------------

  /**
   * "keywords | Group, Group" per line. An empty right-hand side is meaningful:
   * it means this variant takes nothing, which is how a suit's Trousers variant
   * suppresses lining and buttons. A line with no keywords is ignored.
   */
  function parseRules(raw) {
    return String(raw)
      .split('\n')
      .map(function (line) {
        if (line.indexOf('|') === -1) return null;
        var halves = line.split('|');
        var keys = halves[0]
          .split(',')
          .map(function (token) {
            return token.trim().toLowerCase();
          })
          .filter(Boolean);
        if (!keys.length) return null;
        var names = (halves[1] || '')
          .split(',')
          .map(function (token) {
            return token.trim().toLowerCase();
          })
          .filter(Boolean);
        return { keys: keys, groups: names };
      })
      .filter(Boolean);
  }

  /**
   * Knitwear variants read "Single Yarn / Polo with buttons" — the yarn is a
   * separate option and never decides whether buttons exist, so the segment
   * after the slash is what matters.
   */
  function normaliseVariantTitle(title) {
    var text = String(title || '').toLowerCase();
    var parts = text.split('/');
    return parts[parts.length - 1].trim();
  }

  /**
   * @returns {string[]|null} group names, [] for "nothing", null for "no rule"
   *
   * The last segment is tried first for knitwear's sake, then the whole title —
   * because not every slash is a yarn separator. "Jacket traditional /
   * unconstructed" would otherwise be read as just "unconstructed" and match
   * nothing at all.
   */
  function ruleFor(title) {
    var full = String(title || '').toLowerCase().trim();
    var tail = normaliseVariantTitle(title);
    if (!full) return null;

    var candidates = tail && tail !== full ? [tail, full] : [full];
    for (var c = 0; c < candidates.length; c += 1) {
      for (var i = 0; i < rules.length; i += 1) {
        for (var j = 0; j < rules[i].keys.length; j += 1) {
          if (candidates[c].indexOf(rules[i].keys[j]) !== -1) return rules[i].groups;
        }
      }
    }
    return null;
  }

  function appliesToType(group) {
    if (!config.garment) return false;
    var applies = group.appliesTo || [];
    for (var i = 0; i < applies.length; i += 1) {
      if (String(applies[i]).trim().toLowerCase() === config.garment) return true;
    }
    return false;
  }

  /**
   * Variant rules and product type are an intersection, not alternatives.
   * "Traditional make" is a suit variant and also a Pants variant; the rule says
   * it could take a lining, the product type says Pants has none, and only
   * agreeing on both keeps trousers from being offered suit linings.
   */
  function resolveGroups() {
    var byType = allGroups.filter(appliesToType);
    var allowed = ruleFor(currentVariantTitle());

    groups = allowed === null
      ? byType
      : byType.filter(function (group) {
          return allowed.indexOf(group.key.toLowerCase()) !== -1;
        });

    // Drop anything chosen for a group this variant no longer offers, so a
    // lining picked for the jacket cannot follow the customer onto the trousers.
    Object.keys(selection).forEach(function (key) {
      var stillOffered = groups.some(function (group) {
        return group.key === key;
      });
      if (!stillOffered) delete selection[key];
    });

    if (!groups.length) {
      activeGroup = null;
    } else if (!groups.some(function (group) { return group.key === activeGroup; })) {
      activeGroup = groups[0].key;
    }

    syncForm();
    paintLaunchers();
    if (root && !root.hidden) render();
  }

  function currentVariantTitle() {
    if (currentVariant && currentVariant.title) return currentVariant.title;
    var id = readVariantId();
    var list = config.variants || [];
    for (var i = 0; i < list.length; i += 1) {
      if (String(list[i].id) === String(id)) return list[i].title;
    }
    return list.length === 1 ? list[0].title : '';
  }

  function readVariantId() {
    var hidden = document.querySelector('#variant-selected');
    if (hidden && hidden.value) return hidden.value;
    var select = document.querySelector('#product-select');
    return select && select.value ? select.value : null;
  }

  /**
   * The theme drives its custom size picker by setting #product-select and
   * firing change (templates/product.liquid), then mirrors the id onto the
   * hidden #variant-selected. Watching both covers the custom UI and any direct
   * manipulation; the poll is the fallback for app-injected pickers that set the
   * value without dispatching anything.
   */
  function watchVariant() {
    var select = document.querySelector('#product-select');
    var hidden = document.querySelector('#variant-selected');
    if (!select && !hidden) return;

    var last = readVariantId();

    function check() {
      var next = readVariantId();
      if (String(next) === String(last)) return;
      last = next;
      currentVariant = null;
      resolveGroups();
    }

    if (select) select.addEventListener('change', check);
    if (hidden) hidden.addEventListener('change', check);
    document.addEventListener('variant:changed', check);
    window.setInterval(check, 400);
  }

  function bind() {
    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-oc-dismiss]') || event.target.closest('[data-oc-done]')) {
        event.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (root.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab') {
        trapFocus(event);
      }
    });

    document.addEventListener('click', function (event) {
      var launcher = event.target.closest('[data-oc-open]');
      if (!launcher) return;
      event.preventDefault();
      open(launcher.getAttribute('data-oc-open') || null);
    });

    document.addEventListener('click', onPanelClick);

    // The name lives in one line rather than under every swatch, so it has to
    // track whatever the customer is actually looking at.
    if (canHover()) {
      root.addEventListener('pointerover', onHoverDetail);
      root.addEventListener('pointerleave', restoreDetail);
    }
    // Keyboard focus drives the line on every device.
    root.addEventListener('focusin', onHoverDetail);

    // Capture phase, so this runs before the theme's own jQuery click handler on
    // #add (assets/shop.js.liquid) and before the native submit on #add_to_cart.
    // Both add a single item; a configured garment needs several in one request.
    document.addEventListener('click', onAddToCart, true);
  }

  // --- option catalogue ---------------------------------------------------

  /**
   * Options come from the collection's public products.json rather than Liquid:
   * {% paginate %} caps collection.products at 50 per request, and inlining 221
   * linings would ship 284KB to every visitor, most of whom never open this.
   */
  function loadCatalog(handle) {
    if (catalogs[handle]) return Promise.resolve(catalogs[handle]);

    var cached = readCache(handle);
    if (cached) {
      catalogs[handle] = cached;
      return Promise.resolve(cached);
    }

    return fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250', {
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        var options = (payload.products || []).map(toOption).filter(Boolean);
        catalogs[handle] = options;
        writeCache(handle, options);
        return options;
      });
  }

  /**
   * Shopify CDN resizing. products.json hands back the ORIGINAL file, and the
   * linings are ~3.8MB each — a 20-swatch screen was 75MB and the grid visibly
   * failed to fill. width=400 puts the same swatch at 53KB. The width/height
   * attributes on the <img> never helped: they size the box, not the download.
   */
  function sized(src, width) {
    if (!src) return null;
    if (src.indexOf('cdn.shopify.com') === -1 && src.indexOf('/cdn/shop/') === -1) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function toOption(product) {
    var variant = (product.variants || [])[0];
    if (!variant) return null;
    var image = (product.images || [])[0];
    var src = image ? image.src : null;
    return {
      variantId: variant.id,
      title: product.title,
      price: Math.round(parseFloat(variant.price || '0') * 100),
      available: variant.available !== false,
      image: sized(src, 400),
      thumb: sized(src, 160),
      tags: (product.tags || []).map(function (tag) {
        return String(tag).toLowerCase();
      })
    };
  }

  function readCache(handle) {
    try {
      var raw = window.sessionStorage.getItem(CATALOG_CACHE_PREFIX + handle);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.at > CATALOG_TTL_MS) return null;
      return parsed.options;
    } catch (error) {
      return null;
    }
  }

  function writeCache(handle, options) {
    try {
      window.sessionStorage.setItem(
        CATALOG_CACHE_PREFIX + handle,
        JSON.stringify({ at: Date.now(), options: options })
      );
    } catch (error) {
      /* private mode, quota — the catalogue simply refetches */
    }
  }

  /** Options this garment may use, per the config:for:<type> tags. */
  function optionsFor(group) {
    var all = catalogs[group.collection] || [];
    var wanted = TAG_FOR + config.garment;
    return all.filter(function (option) {
      return option.tags.indexOf(wanted) !== -1;
    });
  }

  function groupOf(option) {
    for (var i = 0; i < option.tags.length; i += 1) {
      if (option.tags[i].indexOf(TAG_GROUP) === 0) return option.tags[i].slice(TAG_GROUP.length);
    }
    return null;
  }

  function currentGroup() {
    for (var i = 0; i < groups.length; i += 1) {
      if (groups[i].key === activeGroup) return groups[i];
    }
    return groups[0] || null;
  }

  // --- rendering ----------------------------------------------------------

  function render() {
    var forLine = root.querySelector('[data-oc-for]');
    forLine.textContent = [config.product.title, currentVariantTitle()].filter(Boolean).join(' · ');

    renderTabs();
    renderSummary();

    var group = currentGroup();
    var stage = root.querySelector('[data-oc-stage]');
    var filters = root.querySelector('[data-oc-filters]');

    if (!group) {
      filters.innerHTML = '';
      stage.innerHTML = note(config.labels.notNeeded);
      return;
    }

    if (!catalogs[group.collection]) {
      filters.innerHTML = '';
      stage.innerHTML =
        '<p class="OrderConfig__note"><span class="OrderConfig__spinner" aria-hidden="true"></span>' +
        escapeHtml(config.labels.loading) +
        '</p>';
      loadCatalog(group.collection)
        .then(function () {
          if (currentGroup() === group) render();
        })
        .catch(function () {
          filters.innerHTML = '';
          stage.innerHTML =
            note(config.labels.error, 'OrderConfig__note--error') +
            '<button type="button" class="OrderConfig__retry" data-oc-retry>' +
            escapeHtml(config.labels.retry) +
            '</button>';
        });
      return;
    }

    var options = optionsFor(group);
    if (!options.length) {
      filters.innerHTML = '';
      stage.innerHTML = note(config.labels.empty);
      return;
    }

    renderFilters(group, options);
    renderGrid(group, options);
  }

  function note(text, extra) {
    return '<p class="OrderConfig__note ' + (extra || '') + '">' + escapeHtml(text) + '</p>';
  }

  function renderTabs() {
    var tabs = root.querySelector('[data-oc-tabs]');
    if (groups.length < 2) {
      tabs.innerHTML = '';
      return;
    }
    // A step rail, not a pair of boxes full of product names. The chosen swatch
    // is the status: seeing the material beats reading "Fancy 499 Sand with
    // Walnut Brown Retro Geometric Design" twice on one screen.
    tabs.innerHTML = groups
      .map(function (group, index) {
        var chosen = selection[group.key];
        var isActive = group.key === activeGroup;
        return (
          '<button type="button" role="tab" class="OrderConfig__step" data-oc-tab="' +
          escapeHtml(group.key) +
          '" aria-selected="' + isActive + '">' +
          '<span class="OrderConfig__stepThumb">' +
          (chosen && chosen.image
            ? '<img src="' + escapeHtml(chosen.thumb || chosen.image) + '" alt="" width="72" height="72" loading="lazy">'
            : '<span class="OrderConfig__stepIndex">' + (index + 1) + '</span>') +
          '</span>' +
          '<span class="OrderConfig__stepText">' +
          '<span class="OrderConfig__stepLabel">' + escapeHtml(group.label) + '</span>' +
          '<span class="OrderConfig__stepState">' +
          (chosen ? escapeHtml(config.labels.chosen) : escapeHtml(config.labels.choose)) +
          '</span></span></button>'
        );
      })
      .join('');
  }

  function renderFilters(group, options) {
    var filters = root.querySelector('[data-oc-filters]');
    var present = {};
    options.forEach(function (option) {
      var name = groupOf(option);
      if (name) present[name] = true;
    });

    var ordered = (group.groupOrder || []).filter(function (name) {
      return present[name];
    });
    Object.keys(present)
      .sort()
      .forEach(function (name) {
        if (ordered.indexOf(name) === -1) ordered.push(name);
      });

    if (ordered.length < 2) {
      filters.innerHTML = '';
      activeFilter = null;
      return;
    }

    var pills = [pill('', config.labels.all, !activeFilter)];
    ordered.forEach(function (name) {
      pills.push(pill(name, titleCase(name), activeFilter === name));
    });
    filters.innerHTML = pills.join('');
  }

  function pill(value, label, isActive) {
    return (
      '<button type="button" class="OrderConfig__pill" data-oc-filter="' +
      escapeHtml(value) +
      '" aria-pressed="' +
      Boolean(isActive) +
      '">' +
      escapeHtml(label) +
      '</button>'
    );
  }

  function renderGrid(group, options) {
    var chosen = selection[group.key];
    var visible = activeFilter
      ? options.filter(function (option) {
          return groupOf(option) === activeFilter;
        })
      : options;

    var cards = visible.map(function (option, index) {
      var isChosen = chosen && chosen.variantId === option.variantId;
      var price = option.price > 0 ? money(option.price) : config.labels.included;
      // Only the first screenful cascades; a 197-item stagger would be a
      // several-second wait before the last swatch appeared.
      var delay = index < STAGGER_LIMIT ? ' style="--oc-delay:' + index * 18 + 'ms"' : '';
      // The caption is gone — the swatch is the product, and 197 names turned
      // the grid into a wall of text. The name still has to reach screen
      // readers and the detail line, so it rides on aria-label and data-*.
      var label = option.title + ', ' + (option.available ? price : config.labels.unavailable);
      return (
        '<button type="button" class="OrderConfig__card" data-oc-pick="' +
        option.variantId +
        '" data-oc-name="' + escapeHtml(option.title) +
        '" data-oc-price="' + escapeHtml(price) +
        '" aria-label="' + escapeHtml(label) + '"' +
        delay +
        (option.available ? '' : ' disabled') +
        ' aria-pressed="' +
        Boolean(isChosen) +
        '">' +
        '<span class="OrderConfig__swatch">' +
        (option.image
          ? '<img src="' + escapeHtml(option.image) + '" alt="" loading="lazy" decoding="async" width="240" height="240">'
          : '') +
        '<span class="OrderConfig__check" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5 9 17.5 20 6.5"/></svg></span>' +
        '</span>' +
        (option.available ? '' : '<span class="OrderConfig__soldOut">' + escapeHtml(config.labels.unavailable) + '</span>') +
        '</button>'
      );
    });

    root.querySelector('[data-oc-stage]').innerHTML =
      (group.intro ? '<p class="OrderConfig__intro">' + escapeHtml(group.intro) + '</p>' : '') +
      '<div class="OrderConfig__grid">' + cards.join('') + '</div>';

    showDetail(chosen ? chosen.title : '', chosen ? (chosen.price > 0 ? money(chosen.price) : config.labels.included) : '');
  }

  /** True only where a real pointer can hover, so touch is excluded. */
  function canHover() {
    return !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }

  /**
   * One line, under the grid, naming whatever the pointer or keyboard is on.
   * Dropping captions is what makes the wall of swatches readable; this is what
   * keeps the name available without printing it 197 times.
   *
   * On touch there is no hover, so the "hover a swatch" hint would be an
   * instruction nobody can follow. There the line simply stays empty until
   * something is picked. Its min-height holds the space either way, so the grid
   * never shifts when the line fills.
   */
  function showDetail(name, price) {
    var detail = root.querySelector('[data-oc-detail]');
    if (!detail) return;
    if (!name) {
      detail.innerHTML = canHover()
        ? '<span class="OrderConfig__detailHint">' + escapeHtml(config.labels.detailHint) + '</span>'
        : '';
      return;
    }
    detail.innerHTML =
      '<span class="OrderConfig__detailName">' + escapeHtml(name) + '</span>' +
      (price ? '<span class="OrderConfig__detailPrice">' + escapeHtml(price) + '</span>' : '');
  }

  function renderSummary() {
    var total = 0;
    var thumbs = [];
    var missing = 0;
    groups.forEach(function (group) {
      var chosen = selection[group.key];
      if (!chosen) {
        missing += 1;
        return;
      }
      total += chosen.price;
      thumbs.push(
        '<span class="OrderConfig__chip" title="' + escapeHtml(group.label + ': ' + chosen.title) + '">' +
        (chosen.thumb || chosen.image ? '<img src="' + escapeHtml(chosen.thumb || chosen.image) + '" alt="" width="56" height="56" loading="lazy">' : '') +
        '</span>'
      );
    });

    var summary = root.querySelector('[data-oc-summary]');
    summary.innerHTML = thumbs.length
      ? '<span class="OrderConfig__chips">' + thumbs.join('') + '</span>' +
        '<span class="OrderConfig__summaryText">' +
        (total > 0
          ? escapeHtml(config.labels.extra) + ' <b>' + escapeHtml(money(total)) + '</b>'
          : escapeHtml(config.labels.included)) +
        '</span>'
      : '<span class="OrderConfig__summaryEmpty">' + escapeHtml(config.labels.nothingChosen) + '</span>';

    var done = root.querySelector('[data-oc-done]');
    done.textContent = missing === 0 ? config.labels.done : config.labels.doneLater;
    done.setAttribute('data-complete', missing === 0);
  }

  // --- selection ----------------------------------------------------------

  /**
   * Patch the two cards that changed rather than re-rendering the grid.
   * Re-rendering threw away up to 197 <img> elements on every click, which made
   * the swatches flash as the browser re-decoded them and cancelled the card's
   * own transition mid-flight.
   */
  function pick(card, variantId) {
    var group = currentGroup();
    if (!group) return;

    var options = optionsFor(group);
    var option = null;
    for (var i = 0; i < options.length; i += 1) {
      if (String(options[i].variantId) === String(variantId)) {
        option = options[i];
        break;
      }
    }
    if (!option) return;

    var previous = root.querySelector('.OrderConfig__card[aria-pressed="true"]');
    if (previous && previous !== card) previous.setAttribute('aria-pressed', 'false');
    card.setAttribute('aria-pressed', 'true');

    selection[group.key] = option;
    showDetail(option.title, option.price > 0 ? money(option.price) : config.labels.included);
    renderTabs();
    renderSummary();
    syncForm();
    paintLaunchers();

    // Move to the next unanswered group rather than leaving the customer on a
    // screen they have finished with.
    var next = groups.filter(function (g) { return !selection[g.key]; })[0];
    if (next) {
      window.setTimeout(function () {
        activeGroup = next.key;
        activeFilter = null;
        render();
      }, 260);
    }
  }

  /**
   * Mirror the selection into the /cart/add form as line item properties.
   *
   * The multi-item add below is what actually charges for the options, so these
   * inputs are a safety net: if this script fails after the form is painted, the
   * native POST still records what the customer chose on the garment line.
   */
  function syncForm() {
    var form = document.querySelector('form[action^="/cart/add"]');
    if (!form) return;

    Array.prototype.slice.call(form.querySelectorAll('[data-oc-field]')).forEach(function (node) {
      node.parentNode.removeChild(node);
    });

    groups.forEach(function (group) {
      var chosen = selection[group.key];
      if (!chosen) return;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + group.key + ']';
      input.value = chosen.title;
      input.setAttribute('data-oc-field', group.key);
      form.appendChild(input);
    });
  }

  function paintLaunchers() {
    var complete = groups.length > 0 && groups.every(function (group) {
      return selection[group.key];
    });
    var total = groups.reduce(function (sum, group) {
      var chosen = selection[group.key];
      return sum + (chosen ? chosen.price : 0);
    }, 0);

    Array.prototype.slice.call(document.querySelectorAll('[data-oc-launcher]')).forEach(function (node) {
      // Hidden when the variant needs nothing, and hidden when no variant is
      // resolved at all — the card must not ask for a finish before the
      // customer has said what they are buying.
      node.hidden = groups.length === 0 || !readVariantId();
      node.setAttribute('data-complete', complete);

      var slot = node.querySelector('[data-oc-launcher-rows]');
      if (slot) {
        // Only what has actually been chosen. The card used to list every
        // group up front with an empty swatch and the word "Choose", which
        // made an untouched configuration the tallest the card ever got —
        // two rows of nothing, above the one control that does anything.
        // A row now means a decision; before any decision there are none,
        // and the card is just its heading and the link.
        slot.innerHTML = groups
          .filter(function (group) {
            return selection[group.key];
          })
          .map(function (group) {
            var chosen = selection[group.key];
            var art = chosen.thumb || chosen.image;
            return (
              '<li class="OrderConfigCta__row">' +
              '<span class="OrderConfigCta__thumb">' +
              (art
                ? '<img src="' + escapeHtml(art) + '" alt="" width="88" height="88" loading="lazy">'
                : '') +
              '</span>' +
              '<span class="OrderConfigCta__text">' +
              '<span class="OrderConfigCta__label">' + escapeHtml(group.label) + '</span>' +
              '<span class="OrderConfigCta__value">' + escapeHtml(chosen.title) +
              '</span></span>' +
              '<span class="OrderConfigCta__price">' +
              (chosen.price > 0 ? escapeHtml(money(chosen.price)) : escapeHtml(config.labels.included)) +
              '</span></li>'
            );
          })
          .join('');
      }

      var button = node.querySelector('[data-oc-open]');
      if (button) {
        // The label names only what this garment offers: a shirt has no lining,
        // so it reads "Choose your buttons". A button text set by hand in the
        // theme settings (anything but the old fixed default) is kept as written.
        var customCta = node.getAttribute('data-oc-cta') || '';
        var startText = customCta;
        if ((!customCta || customCta === 'Choose your lining and buttons') && groups.length) {
          var prefix = node.getAttribute('data-oc-cta-prefix') || 'Choose your ';
          var joiner = node.getAttribute('data-oc-cta-and') || ' and ';
          var names = groups.map(function (group) {
            return String(group.label || group.key).toLowerCase();
          });
          startText = prefix + (names.length > 1
            ? names.slice(0, -1).join(', ') + joiner + names[names.length - 1]
            : names[0]);
        }
        button.textContent = complete
          ? config.labels.editConfiguration
          : (startText || config.labels.startConfiguration);
      }

      var totalSlot = node.querySelector('[data-oc-launcher-total]');
      if (totalSlot) {
        totalSlot.innerHTML = complete && total > 0
          ? escapeHtml(config.labels.extra) + ' <b>' + escapeHtml(money(total)) + '</b>'
          : '';
      }
    });
  }

  // --- dialog -------------------------------------------------------------

  function onPanelClick(event) {
    if (!root || root.hidden) return;

    var tab = event.target.closest('[data-oc-tab]');
    if (tab) {
      activeGroup = tab.getAttribute('data-oc-tab');
      activeFilter = null;
      render();
      return;
    }

    var filter = event.target.closest('[data-oc-filter]');
    if (filter) {
      activeFilter = filter.getAttribute('data-oc-filter') || null;
      render();
      return;
    }

    if (event.target.closest('[data-oc-retry]')) {
      render();
      return;
    }

    var card = event.target.closest('[data-oc-pick]');
    if (card && !card.disabled) pick(card, card.getAttribute('data-oc-pick'));
  }

  function onHoverDetail(event) {
    var card = event.target.closest('[data-oc-pick]');
    if (!card) return;
    showDetail(card.getAttribute('data-oc-name'), card.getAttribute('data-oc-price'));
  }

  function restoreDetail() {
    var group = currentGroup();
    var chosen = group && selection[group.key];
    showDetail(
      chosen ? chosen.title : '',
      chosen ? (chosen.price > 0 ? money(chosen.price) : config.labels.included) : ''
    );
  }

  function open(groupKey) {
    if (!groups.length) return;
    if (groupKey) {
      groups.forEach(function (group) {
        if (group.key === groupKey) activeGroup = groupKey;
      });
    }
    activeFilter = null;
    lastFocus = document.activeElement;

    lockScroll(true);
    root.hidden = false;
    render();
    // Force a frame so the browser has the closed state to transition FROM;
    // without it the dialog jumps straight to its open position.
    void root.offsetWidth;
    root.setAttribute('data-state', 'open');
    dialog.focus();
  }

  function close() {
    if (root.hidden) return;
    root.setAttribute('data-state', 'closed');
    lockScroll(false);

    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      root.hidden = true;
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      if (cartLineKey) {
        cartLineKey = null;
        commitCartLine().catch(function () {
          /* the line stays unconfigured and keeps prompting */
        });
      }
    };

    // transitionend is the signal, but it never fires under
    // prefers-reduced-motion, so the timeout is the guaranteed path.
    dialog.addEventListener('transitionend', function handler(event) {
      if (event.target !== dialog) return;
      dialog.removeEventListener('transitionend', handler);
      finish();
    });
    window.setTimeout(finish, 320);
  }

  /**
   * Locking the body without compensating for the scrollbar shifts the whole
   * page sideways as the dialog opens — the one moment the customer is looking
   * straight at it.
   */
  function lockScroll(locked) {
    var doc = document.documentElement;
    if (locked) {
      var gap = window.innerWidth - doc.clientWidth;
      if (gap > 0) doc.style.setProperty('--oc-scrollbar', gap + 'px');
      doc.classList.add('oc-open');
    } else {
      doc.classList.remove('oc-open');
      doc.style.removeProperty('--oc-scrollbar');
    }
  }

  function trapFocus(event) {
    var focusable = dialog.querySelectorAll(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // --- add to cart --------------------------------------------------------

  function onAddToCart(event) {
    var button = event.target.closest('#add, #add_to_cart');
    if (!button || submitting) return;

    var form = button.closest('form[action^="/cart/add"]');
    if (!form) return;

    var items = buildItems(form);
    if (!items || items.length < 2) return; // nothing configured — leave the theme alone

    event.preventDefault();
    event.stopImmediatePropagation();
    submitting = true;
    button.classList.add('is-busy');

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        window.location.href = '/cart';
      })
      .catch(function () {
        // Fall back to the theme's own path rather than stranding the customer:
        // the hidden inputs still carry the choices on the garment line.
        submitting = false;
        button.classList.remove('is-busy');
        form.submit();
      });
  }

  function buildItems(form) {
    var variantId = readVariantId();
    if (!variantId) return null;

    var quantity = garmentQuantity(form);
    var configId = makeId();
    var garmentProperties = { _config_id: configId };
    var addOns = [];

    groups.forEach(function (group) {
      var chosen = selection[group.key];
      if (!chosen) return;
      garmentProperties[group.key] = chosen.title;
      addOns.push({
        id: chosen.variantId,
        quantity: quantity,
        properties: {
          _config_id: configId,
          _config_role: group.key.toLowerCase(),
          // Visible so the pairing survives anywhere the theme does not group
          // lines: the order in admin, the confirmation email, the packing slip.
          For: [config.product.title, currentVariantTitle()].filter(Boolean).join(' · ')
        }
      });
    });

    if (!addOns.length) return null;
    return [{ id: variantId, quantity: quantity, properties: garmentProperties }].concat(addOns);
  }

  function garmentQuantity(form) {
    var field = form.querySelector('select[name="quantity"], input[name="quantity"]');
    var value = field ? parseInt(field.value, 10) : 1;
    return value > 0 ? value : 1;
  }

  // --- cart ---------------------------------------------------------------

  /**
   * Cart-side behaviour: nest add-on lines under their garment, keep their
   * quantities in step, and let an unconfigured line be configured in place.
   *
   * Driven off cart-modern.js's own `cart:updated` event rather than patched
   * into it. That file owns the live cart; reconciling after the fact keeps this
   * out of its request path, and its refresh() replaces [data-cart-items]
   * wholesale so anything done on load has to re-run anyway.
   */
  function initCart() {
    var cart = document.querySelector('[data-cart-root]');
    if (!cart) return;

    applyCartGrouping();
    document.addEventListener('cart:updated', function () {
      window.setTimeout(applyCartGrouping, 0);
    });

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-oc-open-line]');
      if (!trigger) return;
      event.preventDefault();
      openForCartLine(trigger.closest('.LineItem'));
    });
  }

  function applyCartGrouping() {
    nestAddOns();
    reconcileQuantities();
  }

  /**
   * Move each add-on directly beneath its garment. cart.items order does not
   * guarantee they are adjacent — a second suit added later lands between a
   * first suit and its lining — so Liquid cannot do this.
   */
  function nestAddOns() {
    var list = document.querySelector('[data-cart-items]');
    if (!list) return;

    var rows = Array.prototype.slice.call(list.querySelectorAll('.LineItem'));
    var garments = {};
    rows.forEach(function (row) {
      var id = row.getAttribute('data-config-id');
      if (id && !row.getAttribute('data-config-role')) garments[id] = row;
    });

    rows.forEach(function (row) {
      if (!row.getAttribute('data-config-role')) return;
      var garment = garments[row.getAttribute('data-config-id')];
      if (!garment) return;

      var anchor = garment;
      while (
        anchor.nextElementSibling &&
        anchor.nextElementSibling.getAttribute('data-config-id') === row.getAttribute('data-config-id') &&
        anchor.nextElementSibling !== row
      ) {
        anchor = anchor.nextElementSibling;
      }
      if (anchor.nextElementSibling !== row) {
        anchor.parentNode.insertBefore(row, anchor.nextElementSibling);
      }
    });
  }

  /**
   * An add-on exists per garment, so two suits need two linings. The add-on's
   * own quantity control is hidden, which leaves the garment's stepper as the
   * only driver — and a garment removed entirely must take its add-ons with it.
   */
  function reconcileQuantities() {
    var list = document.querySelector('[data-cart-items]');
    if (!list) return;

    var rows = Array.prototype.slice.call(list.querySelectorAll('.LineItem'));
    var wanted = {};
    rows.forEach(function (row) {
      var id = row.getAttribute('data-config-id');
      if (id && !row.getAttribute('data-config-role')) {
        wanted[id] = parseInt(row.getAttribute('data-line-quantity'), 10) || 0;
      }
    });

    var updates = {};
    var changed = false;
    rows.forEach(function (row) {
      if (!row.getAttribute('data-config-role')) return;
      var id = row.getAttribute('data-config-id');
      var target = Object.prototype.hasOwnProperty.call(wanted, id) ? wanted[id] : 0;
      var current = parseInt(row.getAttribute('data-line-quantity'), 10) || 0;
      if (current !== target) {
        updates[row.getAttribute('data-key')] = target;
        changed = true;
      }
    });

    if (!changed) return;

    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return refreshCartRegion();
      })
      .catch(function () {
        /* leave the cart as the server rendered it rather than half-correcting */
      });
  }

  /**
   * Re-render the cart regions after a corrective update. Mirrors the regions
   * cart-modern.js swaps in its own refresh(), because that function is private
   * to it and a full page reload on a quantity change reads as a bug.
   */
  function refreshCartRegion() {
    var cart = document.querySelector('[data-cart-root]');
    if (!cart) return Promise.resolve();

    return fetch(cart.getAttribute('data-cart-url') || '/cart', {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var next = doc.querySelector('[data-cart-root]');
        if (!next) return;

        ['[data-cart-items]', '[data-summary-body]', '[data-ship-meter]', '[data-cart-count]', '[data-sticky-total]'].forEach(
          function (selector) {
            var from = next.querySelector(selector);
            var to = cart.querySelector(selector);
            if (from && to) to.innerHTML = from.innerHTML;
          }
        );
        nestAddOns();
      });
  }

  /** Configure an existing cart line in place. */
  function openForCartLine(row) {
    if (!row) return;
    config.garment = (row.getAttribute('data-config-type') || '').toLowerCase();
    config.product.title = row.getAttribute('data-line-title') || '';
    currentVariant = { title: row.getAttribute('data-line-variant-title') || '' };
    selection = {};
    resolveGroups();
    if (!groups.length) return;
    cartLineKey = row.getAttribute('data-key');
    open(null);
  }

  /**
   * Commit a cart-mode configuration: stamp the properties onto the existing
   * garment line, then add the chosen options as their own lines.
   *
   * /cart/change.js replaces a line's properties wholesale and mints a new line
   * key, so the add-on lines are added afterwards against the id generated here
   * rather than against the old key.
   */
  function commitCartLine() {
    var key = cartLineKey;
    var row = document.querySelector('.LineItem[data-key="' + key + '"]');
    if (!row) return Promise.resolve();

    var quantity = parseInt(row.getAttribute('data-line-quantity'), 10) || 1;
    var configId = makeId();
    var properties = { _config_id: configId };
    var addOns = [];

    groups.forEach(function (group) {
      var chosen = selection[group.key];
      if (!chosen) return;
      properties[group.key] = chosen.title;
      addOns.push({
        id: chosen.variantId,
        quantity: quantity,
        properties: {
          _config_id: configId,
          _config_role: group.key.toLowerCase(),
          For: row.getAttribute('data-line-title') || ''
        }
      });
    });

    if (!addOns.length) return Promise.resolve();

    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, properties: properties })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: addOns })
        });
      })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return refreshCartRegion();
      });
  }

  // --- helpers ------------------------------------------------------------

  function makeId() {
    return 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  function money(cents) {
    var amount = (cents / 100).toFixed(2);
    var format = config.moneyFormat || '{{amount}}';
    return format.replace(/\{\{\s*amount[^}]*\}\}/g, amount);
  }

  function titleCase(text) {
    return String(text).charAt(0).toUpperCase() + String(text).slice(1);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
